import React, { useState, useEffect, useCallback } from "react";
import "./styles.css";

const terms = [
    // Flow Details and Connection Descriptions
    { 
      term: "Flow Time Context", 
      meaning: "Indicates when the information flow occurs or how recently it takes place—e.g., real-time, periodic, on-demand.", 
      category: "flow",
      details: "This helps determine the timing of communications, their responsiveness, and potential latency concerns. For example, a 'Recent' flow implies the data/commands were issued or updated very recently, and the system must support timely processing and delivery."
    },
    { 
      term: "Flow Spatial Context", 
      meaning: "Describes the geographical or network scope of the flow — i.e., where the data travels (local, regional, wide-area) or the logical zones involved.", 
      category: "flow",
      details: "A 'Local' spatial context means the source and destination are within the same facility, campus, or network segment. This affects network design, latency, trust boundaries, and potential vulnerabilities of crossing zones."
    },
    { 
      term: "Flow Cardinality", 
      meaning: "Defines the communication pattern of the flow — for instance unicast (one-to-one), multicast (one-to-many), broadcast (one-to-all).", 
      category: "flow",
      details: "'Unicast' here means the communication is from a single source to a single destination. This guides how many endpoints are involved, how traffic is managed, and how security/control mechanisms scale."
    },
    { 
      term: "Flow Control", 
      meaning: "Specifies what mechanisms are used to manage the communication flow — for example acknowledgement, retries, flow-control, ordering.", 
      category: "flow",
      details: "'Receipt acknowledged' implies that when the source sends data, it expects a confirmation from the destination that the data was successfully received. This increases reliability and supports integrity of the communication."
    },
    { 
      term: "Flow Security", 
      meaning: "Describes the security protections applied to the flow such as encryption, authentication, integrity checking.", 
      category: "flow",
      details: "'Encrypted, Authenticated' means the data in transit is both encrypted (confidentiality) and the communicating parties are verified (authentication). This matches NIST's emphasis on confidentiality and integrity for information security."
    },
    { 
      term: "Initiator", 
      meaning: "Identifies which system starts the flow — either the 'Source' or the 'Destination.'", 
      category: "flow",
      details: "In your description the initiator is 'Source,' meaning the source system sends/control commands or data proactively rather than waiting for a pull request. This distinction matters for architecture, timing, and control considerations."
    },
    { 
      term: "Information Flow", 
      meaning: "A narrative or description of what is being exchanged between source and destination: the content, purpose and functional action of the flow.", 
      category: "flow",
      details: "For example: 'to configure and control threat sensors, object, motion and intrusion detection sensors; to configure and control audio and video surveillance systems.' This gives operational meaning and links the technical attributes to real-world system functions."
    },
    { 
      term: "Equipments", 
      meaning: "The hardware and devices participating in or affected by this flow — both the originators and receivers of data.", 
      category: "flow",
      details: "Listed equipments may include: CCTV cameras, audio systems, sensors (chemical, biological, radiological, explosives), object detection (metal detectors), motion/intrusion detection. Knowing the equipment helps map the information flows to physical assets and helps in threat modelling and control selection."
    },
    { 
      term: "Flow Confidentiality", 
      meaning: "The degree to which the information flow requires protection from unauthorized disclosure.", 
      category: "flow",
      details: "NIST Context: Confidentiality means preserving authorized restrictions on access and disclosure of information. 'Moderate' designation means the data is not the most sensitive (e.g., not top-secret) but still requires protection because unauthorized access could degrade operations or compromise safety."
    },
    { 
      term: "Flow Integrity", 
      meaning: "The degree to which the information must remain accurate and un-modified during transmission or processing.", 
      category: "flow",
      details: "NIST Context: Integrity means guarding against improper information modification or destruction, ensuring the information is reliable and authentic. 'High' indicates that any modification of this flow (accidental or malicious) could have serious operational or safety consequences — e.g., incorrect sensor configuration or intrusion detection commands."
    },
    { 
      term: "Flow Availability", 
      meaning: "The degree to which the information flow must be accessible and usable when needed by authorized parties.", 
      category: "flow",
      details: "NIST Context: Availability means ensuring timely and reliable access to and use of information by authorized individuals. 'Moderate' indicates that the flow must be reliably available under normal operations, but some delay or limited downtime might be tolerable without immediate catastrophic impact. It still requires planning for continuity and fault-tolerance."
    },

    // CWE Vulnerabilities
    { 
      term: "CWE-787: Out-of-Bounds Write", 
      meaning: "Writing data past allocated buffer boundaries, leading to crashes or code execution.", 
      category: "vulnerability",
      details: "This vulnerability occurs when a program writes data beyond the boundaries of allocated memory buffers. It can lead to memory corruption, crashes, or even remote code execution. Common causes include improper bounds checking, integer overflow, and off-by-one errors. Mitigation includes proper input validation, bounds checking, and using safe programming practices."
    },
    { 
      term: "CWE-125: Out-of-Bounds Read", 
      meaning: "Reading outside buffer limits, exposing sensitive data or causing errors.", 
      category: "vulnerability",
      details: "This vulnerability allows attackers to read memory outside the intended buffer boundaries. It can expose sensitive information like passwords, encryption keys, or other confidential data. The vulnerability often occurs due to improper array indexing or buffer size calculations. Prevention includes thorough bounds checking and secure coding practices."
    },
    { 
      term: "CWE-20: Improper Input Validation", 
      meaning: "Failure to validate input, allowing injection attacks or logic errors.", 
      category: "vulnerability",
      details: "This vulnerability occurs when applications fail to properly validate, sanitize, or reject input data. It can lead to various injection attacks including SQL injection, command injection, and cross-site scripting. Proper input validation should include type checking, length validation, format validation, and business rule validation."
    },
    { 
      term: "CWE-77: Command Injection", 
      meaning: "Improper input handling lets attackers execute arbitrary commands.", 
      category: "vulnerability",
      details: "Command injection occurs when an application passes unsafe user input to a system shell. Attackers can execute arbitrary commands with the privileges of the vulnerable application. This is particularly dangerous in web applications that execute system commands. Prevention includes input validation, parameterized commands, and avoiding command execution when possible."
    },
    { 
      term: "CWE-190: Integer Overflow or Wraparound", 
      meaning: "Arithmetic overflow leads to unexpected behavior or security flaws.", 
      category: "vulnerability",
      details: "Integer overflow occurs when arithmetic operations exceed the maximum value that can be stored in a data type. This can lead to unexpected behavior, crashes, or security vulnerabilities. Common in buffer size calculations and array indexing. Prevention includes using larger data types, bounds checking, and safe arithmetic operations."
    },
    { 
      term: "CWE-79: Cross-Site Scripting (XSS)", 
      meaning: "Injecting scripts into web pages to execute code in a user's browser.", 
      category: "vulnerability",
      details: "XSS allows attackers to inject malicious scripts into web pages viewed by other users. This can lead to session hijacking, data theft, or defacement. Types include reflected XSS, stored XSS, and DOM-based XSS. Prevention includes input validation, output encoding, and Content Security Policy (CSP) headers."
    },
    { 
      term: "CWE-89: SQL Injection", 
      meaning: "Injecting malicious SQL queries via unvalidated input to manipulate databases.", 
      category: "vulnerability",
      details: "SQL injection occurs when user input is directly concatenated into SQL queries without proper validation or parameterization. Attackers can manipulate database queries to access, modify, or delete data. Prevention includes using parameterized queries, input validation, and least privilege database access."
    },
    { 
      term: "CWE-352: Cross-Site Request Forgery (CSRF)", 
      meaning: "Tricking users into executing unintended actions in authenticated sessions.", 
      category: "vulnerability",
      details: "CSRF attacks trick authenticated users into performing actions they didn't intend, such as changing passwords or making purchases. The attack works by exploiting the trust between a user's browser and a website. Prevention includes CSRF tokens, SameSite cookies, and proper session management."
    },
    { 
      term: "CWE-78: OS Command Injection", 
      meaning: "Inserting malicious commands in OS-level instructions, compromising systems.", 
      category: "vulnerability",
      details: "OS command injection allows attackers to execute arbitrary operating system commands through a vulnerable application. This can lead to complete system compromise, data theft, or system manipulation. Prevention includes input validation, avoiding command execution, and using secure APIs instead of shell commands."
    },
    { 
      term: "CWE-416: Use After Free", 
      meaning: "Accessing memory after it's freed, causing crashes or code execution.", 
      category: "vulnerability",
      details: "Use after free occurs when a program continues to use a pointer after the memory it points to has been freed. This can lead to crashes, data corruption, or code execution. Common in languages without automatic memory management. Prevention includes proper memory management and using memory-safe languages."
    },

    // Mitigation Techniques
    { 
      term: "M1017: User Training", 
      meaning: "Educate users to recognize phishing and malicious activity.", 
      category: "mitigation",
      details: "User training programs educate employees about cybersecurity threats, safe computing practices, and how to recognize social engineering attacks. Training should be regular, engaging, and include practical exercises. Topics include password security, phishing awareness, social media safety, and incident reporting procedures."
    },
    { 
      term: "M1032: Multi-Factor Authentication (MFA)", 
      meaning: "Require multiple credentials for user authentication.", 
      category: "mitigation",
      details: "MFA requires users to provide multiple forms of authentication, typically combining something they know (password), something they have (token), and something they are (biometric). This significantly reduces the risk of unauthorized access even if one factor is compromised. Implementation should consider usability and security balance."
    },
    { 
      term: "M1035: Limit Network Access", 
      meaning: "Restrict remote access using VPNs and strong policies.", 
      category: "mitigation",
      details: "Network access control limits who can access network resources and from where. This includes VPN requirements, network segmentation, and access policies. Implementation should include strong authentication, encryption, and monitoring of network access patterns."
    },
    { 
      term: "M1038: Execution Prevention", 
      meaning: "Prevent unapproved software from running using whitelisting.", 
      category: "mitigation",
      details: "Application whitelisting prevents unauthorized software from executing on systems. This includes approved application lists, code signing requirements, and execution policies. This technique is particularly effective against malware and unauthorized software installation."
    },
    { 
      term: "M1042: Remove Unnecessary Features", 
      meaning: "Disable unused features or services to reduce attack surface.", 
      category: "mitigation",
      details: "Reducing attack surface involves disabling or removing unnecessary services, features, and accounts. This includes removing default accounts, disabling unused ports, and uninstalling unnecessary software. Regular audits should be conducted to identify and remove unnecessary components."
    },
    { 
      term: "M1050: Exploit Protection", 
      meaning: "Deploy anti-exploit tools and OS protections like DEP, ASLR.", 
      category: "mitigation",
      details: "Exploit protection techniques include Data Execution Prevention (DEP), Address Space Layout Randomization (ASLR), and other memory protection mechanisms. These techniques make it harder for attackers to execute code in memory and exploit buffer overflow vulnerabilities."
    },
    { 
      term: "M1030: Network Segmentation", 
      meaning: "Divide network into zones to prevent lateral movement.", 
      category: "mitigation",
      details: "Network segmentation divides networks into smaller, isolated segments to limit the spread of attacks and contain potential breaches. This includes VLANs, firewalls, and access controls between segments. Segmentation should be based on business needs and security requirements."
    },
    { 
      term: "M1047: Audit and Logging", 
      meaning: "Enable monitoring and logs to detect suspicious activity.", 
      category: "mitigation",
      details: "Comprehensive logging and monitoring systems collect and analyze security events to detect suspicious activity. This includes system logs, network logs, application logs, and security event logs. Logs should be centralized, protected, and regularly reviewed for anomalies."
    },
    { 
      term: "M1057: Data Loss Prevention (DLP)", 
      meaning: "Prevent unauthorized data sharing using DLP tools.", 
      category: "mitigation",
      details: "DLP systems monitor, detect, and prevent unauthorized data access, use, or transmission. This includes content inspection, policy enforcement, and user education. DLP should be integrated with existing security controls and tailored to organizational data classification policies."
    },
    { 
      term: "M1053: Data Backup", 
      meaning: "Regularly back up data to ensure recovery after incidents.", 
      category: "mitigation",
      details: "Regular data backups ensure that critical information can be recovered after security incidents, system failures, or disasters. Backup strategies should include multiple copies, off-site storage, encryption, and regular testing of recovery procedures."
    },
    { 
      term: "M1026: Privileged Account Management", 
      meaning: "Restrict and monitor use of high-privilege accounts.", 
      category: "mitigation",
      details: "Privileged account management controls access to high-privilege accounts through policies, monitoring, and access controls. This includes just-in-time access, session recording, and regular privilege reviews. Implementation should balance security with operational needs."
    },
    { 
      term: "M1037: Filter Network Traffic", 
      meaning: "Block malicious traffic using firewalls and IDS/IPS.", 
      category: "mitigation",
      details: "Network traffic filtering uses firewalls, intrusion detection systems (IDS), and intrusion prevention systems (IPS) to block malicious traffic and detect attacks. This includes signature-based detection, anomaly detection, and behavioral analysis."
    },
    { 
      term: "M1029: Remote Data Storage", 
      meaning: "Store critical data securely in remote or cloud locations.", 
      category: "mitigation",
      details: "Remote data storage provides secure, redundant storage of critical data in geographically distributed locations. This includes cloud storage, off-site backups, and disaster recovery sites. Implementation should include encryption, access controls, and regular testing."
    },

    // OWASP Top 10
    { 
      term: "OWASP A1: Injection", 
      meaning: "Exploiting unvalidated input to inject malicious commands or queries.", 
      category: "owasp",
      details: "Injection flaws occur when untrusted data is sent to an interpreter as part of a command or query. Common types include SQL injection, NoSQL injection, LDAP injection, and command injection. Prevention includes input validation, parameterized queries, and proper output encoding."
    },
    { 
      term: "OWASP A2: Broken Authentication", 
      meaning: "Flaws in authentication mechanisms allow unauthorized access.", 
      category: "owasp",
      details: "Broken authentication includes weak passwords, session management flaws, and improper logout functionality. Attackers can exploit these flaws to gain unauthorized access to user accounts. Prevention includes strong password policies, secure session management, and multi-factor authentication."
    },
    { 
      term: "OWASP A3: Sensitive Data Exposure", 
      meaning: "Failure to protect sensitive data with proper encryption.", 
      category: "owasp",
      details: "Sensitive data exposure occurs when applications fail to properly protect sensitive information like passwords, credit card numbers, and personal data. This includes weak encryption, improper key management, and insecure transmission. Prevention includes strong encryption, secure key management, and HTTPS."
    },
    { 
      term: "OWASP A4: XML External Entities (XXE)", 
      meaning: "Exploiting XML parsers to access internal resources.", 
      category: "owasp",
      details: "XXE attacks exploit XML parsers to access internal files, network resources, or execute code. This occurs when XML parsers are configured to process external entities. Prevention includes disabling external entity processing and using secure XML parsers."
    },
    { 
      term: "OWASP A5: Broken Access Control", 
      meaning: "Improper enforcement of access permissions.", 
      category: "owasp",
      details: "Broken access control allows attackers to access unauthorized resources or perform unauthorized actions. This includes insecure direct object references, missing access controls, and privilege escalation. Prevention includes proper access control implementation and regular security testing."
    },
    { 
      term: "OWASP A6: Security Misconfiguration", 
      meaning: "Using default settings or incomplete security configurations.", 
      category: "owasp",
      details: "Security misconfiguration includes default passwords, unnecessary features, and improper security settings. This creates vulnerabilities that attackers can exploit. Prevention includes secure configuration management, regular security audits, and automated configuration testing."
    },
    { 
      term: "OWASP A7: Cross-Site Scripting (XSS)", 
      meaning: "Injecting scripts to execute in user browsers.", 
      category: "owasp",
      details: "XSS allows attackers to inject malicious scripts into web pages viewed by other users. This can lead to session hijacking, data theft, or defacement. Types include reflected XSS, stored XSS, and DOM-based XSS. Prevention includes input validation, output encoding, and Content Security Policy."
    },
    { 
      term: "OWASP A8: Insecure Deserialization", 
      meaning: "Maliciously crafted serialized data leads to code execution.", 
      category: "owasp",
      details: "Insecure deserialization occurs when applications deserialize untrusted data without proper validation. This can lead to remote code execution, privilege escalation, or denial of service. Prevention includes input validation, secure serialization formats, and proper error handling."
    },
    { 
      term: "OWASP A9: Components with Known Vulnerabilities", 
      meaning: "Using outdated components with security flaws.", 
      category: "owasp",
      details: "Using components with known vulnerabilities exposes applications to attacks that exploit these flaws. This includes outdated libraries, frameworks, and third-party components. Prevention includes regular vulnerability scanning, patch management, and component inventory tracking."
    },
    { 
      term: "OWASP A10: Insufficient Logging & Monitoring", 
      meaning: "Failing to detect and monitor attacks effectively.", 
      category: "owasp",
      details: "Insufficient logging and monitoring makes it difficult to detect and respond to security incidents. This includes missing logs, inadequate monitoring, and poor incident response. Prevention includes comprehensive logging, real-time monitoring, and automated alerting systems."
    },

    // NIST Framework
    { 
      term: "NIST Identify", 
      meaning: "Understand organizational assets, risks, and vulnerabilities.", 
      category: "nist",
      details: "The Identify function helps organizations develop an organizational understanding to manage cybersecurity risk to systems, people, assets, data, and capabilities. Activities include asset management, business environment assessment, governance, risk assessment, and risk management strategy."
    },
    { 
      term: "NIST Protect", 
      meaning: "Implement safeguards like encryption and MFA to secure assets.", 
      category: "nist",
      details: "The Protect function outlines appropriate safeguards to ensure delivery of critical infrastructure services. Activities include access control, awareness and training, data security, information protection processes and procedures, maintenance, and protective technology."
    },
    { 
      term: "NIST Detect", 
      meaning: "Continuously monitor to identify anomalies and security events.", 
      category: "nist",
      details: "The Detect function defines the appropriate activities to identify the occurrence of a cybersecurity event. Activities include anomalies and events, security continuous monitoring, and detection processes."
    },
    { 
      term: "NIST Respond", 
      meaning: "Contain and mitigate cybersecurity incidents quickly.", 
      category: "nist",
      details: "The Respond function includes appropriate activities to take action regarding a detected cybersecurity incident. Activities include response planning, communications, analysis, mitigation, and improvements."
    },
    { 
      term: "NIST Recover", 
      meaning: "Restore systems and data to normal operation after an incident.", 
      category: "nist",
      details: "The Recover function identifies appropriate activities to maintain plans for resilience and to restore any capabilities or services that were impaired due to a cybersecurity incident. Activities include recovery planning, improvements, and communications."
    },

    // MITRE ATT&CK
    { 
      term: "T1059: Command and Scripting Interpreter", 
      meaning: "Running malicious commands via interpreters like PowerShell or Bash.", 
      category: "attack",
      details: "Attackers use command and script interpreters to execute malicious commands on systems. This includes PowerShell, Bash, Python, and other scripting languages. Detection includes monitoring command execution, script analysis, and behavioral analysis."
    },
    { 
      term: "T1078: Valid Accounts", 
      meaning: "Use of stolen or compromised credentials for persistence.", 
      category: "attack",
      details: "Attackers use valid accounts to maintain access to systems and networks. This includes default accounts, local accounts, domain accounts, and cloud accounts. Detection includes account monitoring, privilege escalation detection, and unusual access patterns."
    },
    { 
      term: "T1105: Ingress Tool Transfer", 
      meaning: "Transferring tools or malware to a compromised system.", 
      category: "attack",
      details: "Attackers transfer tools, malware, or other files to compromised systems to support their operations. This includes downloading files, uploading tools, and using legitimate services for file transfer. Detection includes network monitoring, file analysis, and behavioral analysis."
    },
    { 
      term: "T1027: Obfuscated Files or Information", 
      meaning: "Hiding malicious code to evade detection.", 
      category: "attack",
      details: "Attackers obfuscate files or information to hide their activities and evade detection. This includes encoding, encryption, compression, and other obfuscation techniques. Detection includes file analysis, behavioral monitoring, and signature-based detection."
    },
    { 
      term: "T1486: Data Encrypted for Impact", 
      meaning: "Encrypting files on victim systems to demand ransom.", 
      category: "attack",
      details: "Attackers encrypt files on victim systems to demand ransom payments. This is a common ransomware technique that can cause significant business disruption. Prevention includes regular backups, network segmentation, and user education about phishing."
    },

    // Concepts
    { 
      term: "Encryption", 
      meaning: "Encoding data to protect confidentiality during storage and transmission.", 
      category: "concept",
      details: "Encryption converts plaintext data into ciphertext using mathematical algorithms and keys. This protects data confidentiality during storage and transmission. Types include symmetric encryption, asymmetric encryption, and hashing. Implementation should include strong algorithms, secure key management, and proper implementation."
    },
    { 
      term: "Least Privilege", 
      meaning: "Restrict user permissions to the minimum required for their tasks.", 
      category: "concept",
      details: "The principle of least privilege restricts user access to only the resources and permissions necessary to perform their job functions. This reduces the potential impact of security incidents and limits lateral movement. Implementation includes role-based access control, regular access reviews, and privilege escalation controls."
    },
    { 
      term: "Patch Management", 
      meaning: "Regularly applying updates to fix vulnerabilities in systems and apps.", 
      category: "concept",
      details: "Patch management is the process of identifying, acquiring, testing, and deploying software updates to fix vulnerabilities and improve security. This includes vulnerability scanning, patch testing, deployment planning, and verification. Effective patch management reduces the attack surface and prevents exploitation of known vulnerabilities."
    },
    { 
      term: "Incident Response", 
      meaning: "Steps to detect, contain, and recover from cyber incidents.", 
      category: "concept",
      details: "Incident response is the systematic approach to handling security incidents. The process includes preparation, identification, containment, eradication, recovery, and lessons learned. Effective incident response minimizes damage, reduces recovery time, and improves future security posture."
    },
    { 
      term: "Zero-Day Vulnerability", 
      meaning: "A flaw unknown to the vendor and actively exploited without a patch.", 
      category: "concept",
      details: "A zero-day vulnerability is a security flaw that is unknown to the software vendor and has no available patch. Attackers may exploit these vulnerabilities before they are discovered and patched. Detection includes behavioral monitoring, anomaly detection, and threat intelligence. Prevention includes defense in depth and security monitoring."
    },
    
    // ATT&CK Technique Glossary
    { 
      term: "T1003 — OS Credential Dumping", 
      meaning: "Techniques used to obtain credentials from a system (password hashes, cleartext credentials, or tokens) for lateral movement or privilege escalation.", 
      category: "attack",
      details: "Adversaries may attempt to dump credentials to obtain account login and credential material, normally in the form of a hash or a clear text password, from the operating system and software. Credentials can then be used to perform lateral movement and access restricted information."
    },
    { 
      term: "T1003.001 — LSASS Memory", 
      meaning: "Extracting credential material from LSASS process memory (e.g., dumping LSASS) to retrieve account credentials.", 
      category: "attack",
      details: "Adversaries may attempt to access credential material stored in the process memory of the Local Security Authority Subsystem Service (LSASS). After a user logs on, the system generates and stores a variety of credential materials in LSASS process memory."
    },
    { 
      term: "T1003.002 — Security Account Manager", 
      meaning: "Extracting credential material from NTDS.dit or other on-disk stores containing AD credentials.", 
      category: "attack",
      details: "Adversaries may attempt to extract credential material from the Security Account Manager (SAM) database either through in-memory techniques or through the Windows Registry where the SAM database is stored."
    },
    { 
      term: "T1003.003 — NTDS", 
      meaning: "Targeting platform or service-specific credential stores to harvest authentication material.", 
      category: "attack",
      details: "Adversaries may attempt to access or create a copy of the Active Directory domain database in order to steal credential information, as well as obtain other information about domain members such as devices, users, and access rights."
    },
    { 
      term: "T1003.004 — LSA Secrets", 
      meaning: "Extracting credentials from specialized storage mechanisms or caches unique to a platform.", 
      category: "attack",
      details: "Adversaries with SYSTEM access to a host may attempt to access Local Security Authority (LSA) secrets, which can contain a variety of different credential materials, such as credentials for service accounts."
    },
    { 
      term: "T1003.005 — Cached Domain Credentials", 
      meaning: "Using memory scraping or volatile memory techniques to collect credentials held in process memory.", 
      category: "attack",
      details: "Adversaries may attempt to access cached domain credentials used to allow authentication to occur in the event a domain controller is unavailable."
    },
    { 
      term: "T1005 — Data from Local System", 
      meaning: "Collecting files directly from local file systems as part of data discovery or exfiltration.", 
      category: "attack",
      details: "Adversaries may search local system sources, such as file systems and configuration files or local databases, to find files of interest and sensitive data prior to exfiltration."
    },
    { 
      term: "T1012 — Query Registry", 
      meaning: "Querying or modifying system registries to gather information, enable persistence, or alter configurations.", 
      category: "attack",
      details: "Adversaries may interact with the Windows Registry to gather information about the system, configuration, and installed software."
    },
    { 
      term: "T1016 — System Network Configuration Discovery", 
      meaning: "Enumerating network interfaces and routing information to understand host networking and plan actions.", 
      category: "attack",
      details: "Adversaries may look for details about the network configuration and settings, such as IP and/or MAC addresses, of systems they access or through information discovery of remote systems."
    },
    { 
      term: "T1018 — Remote System Discovery", 
      meaning: "Scanning for or enumerating services and listening ports on hosts and network endpoints.", 
      category: "attack",
      details: "Adversaries may attempt to get a listing of other systems by IP address, hostname, or other logical identifier on a network that may be used for lateral movement from the current system."
    },
    { 
      term: "T1021 — Remote Services", 
      meaning: "Using remote services (e.g., SSH, RDP, SMB) to execute commands or move laterally between systems.", 
      category: "attack",
      details: "Adversaries may use valid accounts to log into a service specifically designed to accept remote connections, such as telnet, SSH, and VNC. The adversary may then perform actions as the logged-on user."
    },
    { 
      term: "T1027 — Obfuscated Files or Information", 
      meaning: "Employing obfuscation, packing, or encoding to hide malicious code or payloads from analysis.", 
      category: "attack",
      details: "Adversaries may attempt to make an executable or file difficult to discover or analyze by encrypting, encoding, or otherwise obfuscating its contents on the system or in transit."
    },
    { 
      term: "T1027.002 — Software Packing", 
      meaning: "Using interpreter-based obfuscation (e.g., script encoding or embedding) to conceal payloads.", 
      category: "attack",
      details: "Adversaries may perform software packing or virtual machine software protection to conceal their code. Software packing is a method of compressing or encrypting an executable."
    },
    { 
      term: "T1027.004 — Compile After Delivery", 
      meaning: "Packing or binary obfuscation techniques to make executables harder to analyze and detect.", 
      category: "attack",
      details: "Adversaries may attempt to make payloads difficult to discover and analyze by delivering files to victims as uncompiled code."
    },
    { 
      term: "T1033 — System Owner/User Discovery", 
      meaning: "Modifying or interacting with system utilities and built-in tools to facilitate actions or gather information.", 
      category: "attack",
      details: "Adversaries may attempt to identify the primary user, currently logged in user, set of users that commonly uses a system, or whether a user is actively using the system."
    },
    { 
      term: "T1036 — Masquerading", 
      meaning: "Masquerading as legitimate files, services, or processes to avoid detection.", 
      category: "attack",
      details: "Adversaries may attempt to manipulate features of their artifacts to make them appear legitimate or benign to users and/or security tools."
    },
    { 
      term: "T1036.005 — Match Legitimate Name or Location", 
      meaning: "Renaming or placing files/processes in locations to appear legitimate (masquerading by name/path).", 
      category: "attack",
      details: "Adversaries may match or approximate the name or location of legitimate files or resources when naming/placing them. This is done for the sake of evading defenses and observation."
    },
    { 
      term: "T1041 — Exfiltration Over C2 Channel", 
      meaning: "Communicating with command-and-control infrastructure to send or receive data (C2/exfiltration).", 
      category: "attack",
      details: "Adversaries may steal data by exfiltrating it over an existing command and control channel. Stolen data is encoded into the normal communications channel using the same protocol as command and control communications."
    },
    { 
      term: "T1047 — Windows Management Instrumentation", 
      meaning: "Using Windows Management Instrumentation (WMI) or equivalent management frameworks for execution or discovery.", 
      category: "attack",
      details: "Adversaries may abuse Windows Management Instrumentation (WMI) to execute malicious commands and payloads. WMI is an administration feature that provides a uniform environment to access Windows system components."
    },
    { 
      term: "T1053 — Scheduled Task/Job", 
      meaning: "Creating or abusing scheduled tasks or jobs to run code at planned times for persistence or execution.", 
      category: "attack",
      details: "Adversaries may abuse task scheduling functionality to facilitate initial or recurring execution of malicious code."
    },
    { 
      term: "T1053.005 — Scheduled Task", 
      meaning: "Leveraging platform- or service-specific scheduling mechanisms as a subtechnique of scheduled execution.", 
      category: "attack",
      details: "Adversaries may abuse the Windows Task Scheduler to perform task scheduling for initial or recurring execution of malicious code."
    },
    { 
      term: "T1055 — Process Injection", 
      meaning: "Injecting code into other processes to hide execution and evade defenses (process injection).", 
      category: "attack",
      details: "Adversaries may inject code into processes in order to evade process-based defenses as well as possibly elevate privileges."
    },
    { 
      term: "T1059 — Command and Scripting Interpreter", 
      meaning: "Running commands or scripts through system interpreters (PowerShell, Bash, cmd, Python, etc.).", 
      category: "attack",
      details: "Adversaries may abuse command and script interpreters to execute commands, scripts, or binaries."
    },
    { 
      term: "T1059.001 — PowerShell", 
      meaning: "Executing commands specifically via PowerShell to run scripts or payloads.", 
      category: "attack",
      details: "Adversaries may abuse PowerShell commands and scripts for execution. PowerShell is a powerful interactive command-line interface and scripting environment included in the Windows operating system."
    },
    { 
      term: "T1059.003 — Windows Command Shell", 
      meaning: "Using the Windows command shell (cmd.exe) to execute commands or batch scripts.", 
      category: "attack",
      details: "Adversaries may abuse the Windows command shell for execution. The Windows command shell (cmd) is the primary command prompt on Windows systems."
    },
    { 
      term: "T1059.005 — Visual Basic", 
      meaning: "Using application scripting (e.g., JavaScript, VBA) to execute malicious logic, often via documents.", 
      category: "attack",
      details: "Adversaries may abuse Visual Basic (VB) for execution. VB is a programming language created by Microsoft with interoperability with many Windows technologies such as Component Object Model and the Native API."
    },
    { 
      term: "T1068 — Exploitation for Privilege Escalation", 
      meaning: "Exploiting permission or authorization weaknesses (service or application permissions) to escalate privileges.", 
      category: "attack",
      details: "Adversaries may exploit software vulnerabilities in an attempt to elevate privileges. Exploitation of a software vulnerability occurs when an adversary takes advantage of a programming error in a program, service, or within the operating system software or kernel itself to execute adversary-controlled code."
    },
    { 
      term: "T1070 — Indicator Removal on Host", 
      meaning: "Modifying, deleting, or tampering with logs and forensic artifacts to cover tracks.", 
      category: "attack",
      details: "Adversaries may delete or modify artifacts generated within systems to remove evidence of their presence or hinder defenses."
    },
    { 
      term: "T1070.004 — File Deletion", 
      meaning: "Targeting Windows event logs or equivalents for log clearing or manipulation.", 
      category: "attack",
      details: "Adversaries may delete files left behind by the actions of their intrusion activity. Malware, tools, or other non-native files dropped or created on a system by an adversary may leave traces to indicate to what was done within a network and how."
    },
    { 
      term: "T1070.006 — Timestomp", 
      meaning: "Tampering with alternate log sources (file logs, custom logs) to hinder detection.", 
      category: "attack",
      details: "Adversaries may modify file time attributes to hide new or changes to existing files. Timestomping is a technique that modifies the timestamps of a file (the modify, access, create, and change times), often to mimic files that are in the same folder."
    },
    { 
      term: "T1071 — Application Layer Protocol", 
      meaning: "Using application-layer protocols (HTTP/S, DNS, SMTP, etc.) as channels for C2 or data transfer.", 
      category: "attack",
      details: "Adversaries may communicate using application layer protocols to avoid detection/network filtering by blending in with existing traffic."
    },
    { 
      term: "T1071.001 — Web Protocols", 
      meaning: "Using HTTP or HTTPS as the primary protocol for command-and-control communication.", 
      category: "attack",
      details: "Adversaries may communicate using application layer protocols associated with web traffic to avoid detection/network filtering by blending in with existing traffic."
    },
    { 
      term: "T1071.004 — DNS", 
      meaning: "Using protocols like DNS or other commonly allowed protocols to tunnel or hide malicious traffic.", 
      category: "attack",
      details: "Adversaries may communicate using the Domain Name System (DNS) application layer protocol to avoid detection/network filtering by blending in with existing traffic."
    },
    { 
      term: "T1074 — Data Staged", 
      meaning: "Using legitimate data transfer services (cloud storage, file shares) to stage or move data.", 
      category: "attack",
      details: "Adversaries may stage collected data in a central location or directory prior to exfiltration. Data may be kept in separate files or combined into one file through techniques such as Archive Collected Data."
    },
    { 
      term: "T1078 — Valid Accounts", 
      meaning: "Leveraging valid credentials (stolen or compromised accounts) to access systems and maintain persistence.", 
      category: "attack",
      details: "Adversaries may obtain and abuse credentials of existing accounts as a means of gaining initial access, persistence, privilege escalation, or defense evasion."
    },
    { 
      term: "T1082 — System Information Discovery", 
      meaning: "Gathering system information (OS, hardware, installed software) to profile a host for follow-on actions.", 
      category: "attack",
      details: "An adversary may attempt to get detailed information about the operating system and hardware, including version, patches, hotfixes, service packs, and architecture."
    },
    { 
      term: "T1083 — File and Directory Discovery", 
      meaning: "Enumerating files and directories to locate data of interest or targets for exfiltration.", 
      category: "attack",
      details: "Adversaries may enumerate files and directories or may search in specific locations of a host or network share for certain information within a file system."
    },
    { 
      term: "T1090 — Proxy", 
      meaning: "Using proxying, tunneling, or other relay services to obscure traffic origin or bypass controls.", 
      category: "attack",
      details: "Adversaries may use a connection proxy to direct network traffic between systems or act as an intermediary for network communications to a command and control server to avoid direct connections to their infrastructure."
    },
    { 
      term: "T1090.001 — Internal Proxy", 
      meaning: "Routing traffic through external proxy services or compromise-as-a-proxy infrastructures.", 
      category: "attack",
      details: "Adversaries may use an internal proxy to direct command and control traffic between systems in a compromised environment."
    },
    { 
      term: "T1098 — Account Manipulation", 
      meaning: "Abusing authentication mechanisms or services to gain or maintain access (token misuse, replay, etc.).", 
      category: "attack",
      details: "Adversaries may manipulate accounts to maintain access to victim systems. Account manipulation may consist of any action that preserves adversary access to a compromised account."
    },
    { 
      term: "T1098.004 — SSH Authorized Keys", 
      meaning: "Specific subtechniques abusing tokens or delegated authentication mechanisms.", 
      category: "attack",
      details: "Adversaries may modify the SSH authorized_keys file to maintain persistence on a victim host. Linux distributions and macOS commonly use key-based authentication to secure the authentication process of SSH sessions for remote management."
    },
    { 
      term: "T1102 — Web Service", 
      meaning: "Using web services, cloud providers, or third-party hosting for command, control, or data staging.", 
      category: "attack",
      details: "Adversaries may use an existing, legitimate external Web service to relay data to/from a compromised system to avoid suspicion."
    },
    { 
      term: "T1105 — Ingress Tool Transfer", 
      meaning: "Transferring tools or files to a compromised environment (ingress tool transfer) to support operations.", 
      category: "attack",
      details: "Adversaries may transfer tools or other files from an external system into a compromised environment. Tools may be transferred in the course of an operation but may also be staged ahead of time onto systems that are not internet-connected but have network access to the compromised environment."
    },
    { 
      term: "T1110 — Brute Force", 
      meaning: "Attempting to obtain valid credentials through brute force, password spraying, or credential stuffing.", 
      category: "attack",
      details: "Adversaries may use brute force techniques to gain access to accounts when passwords are unknown or when password hashes are obtained."
    },
    { 
      term: "T1110.001 — Password Guessing", 
      meaning: "Conducting password spraying or brute-force attacks against authentication interfaces.", 
      category: "attack",
      details: "Adversaries with no prior knowledge of legitimate credentials within the system or environment may guess passwords to attempt access to accounts."
    },
    { 
      term: "T1110.003 — Password Spraying", 
      meaning: "Conducting credential stuffing using previously leaked username/password pairs.", 
      category: "attack",
      details: "Adversaries may use a single or small list of commonly used passwords against many different accounts to attempt to acquire valid account credentials."
    },
    { 
      term: "T1112 — Modify Registry", 
      meaning: "Modifying or abusing authentication mechanisms, tokens, or credentials on a target system.", 
      category: "attack",
      details: "Adversaries may interact with the Windows Registry to hide configuration information within Registry keys, remove information as part of cleaning up, or as part of other techniques to aid in persistence and execution."
    },
    { 
      term: "T1113 — Screen Capture", 
      meaning: "Collecting data from local sources (files, caches, containers) to harvest sensitive information.", 
      category: "attack",
      details: "Adversaries may attempt to take screen captures of the desktop to gather information over the course of an operation."
    },
    { 
      term: "T1114 — Email Collection", 
      meaning: "Intercepting or collecting email content from local mail clients or mail stores.", 
      category: "attack",
      details: "Adversaries may target user email to collect sensitive information. Emails may contain sensitive data, including trade secrets or personal information, that can prove valuable to adversaries."
    },
    { 
      term: "T1114.002 — Remote Email Collection", 
      meaning: "Targeting specific mail client storage or protocols to exfiltrate email data.", 
      category: "attack",
      details: "Adversaries may target an Exchange server, Office 365, or Google Workspace to collect mail from victim accounts."
    },
    { 
      term: "T1119 — Automated Collection", 
      meaning: "Using automated collection scripts or tools to gather and stage data for exfiltration.", 
      category: "attack",
      details: "Once established within a system or network, an adversary may use automated techniques for collecting internal data."
    },
    { 
      term: "T1124 — System Time Discovery", 
      meaning: "Abusing trusted delegations or relationships (e.g., mailbox delegates) to gain access.", 
      category: "attack",
      details: "An adversary may gather the system time and/or time zone from a local or remote system. The system time is set and stored by the Windows Time Service within a domain to maintain time synchronization between systems and services in an enterprise network."
    },
    { 
      term: "T1133 — External Remote Services", 
      meaning: "Abusing external remote services (third-party VPNs, collaboration tools) to pivot into networks.", 
      category: "attack",
      details: "Adversaries may leverage external-facing remote services to initially access and/or persist within a network. Remote services such as VPNs, Citrix, and other access mechanisms allow users to connect to internal enterprise network resources from external locations."
    },
    { 
      term: "T1134 — Access Token Manipulation", 
      meaning: "Manipulating cloud or platform permissions to escalate privileges or widen access.", 
      category: "attack",
      details: "Adversaries may modify access tokens to operate under a different user or system security context to perform actions and bypass access controls."
    },
    { 
      term: "T1134.001 — Token Impersonation/Theft", 
      meaning: "Abusing cloud service roles or permissions to increase privilege or persistence.", 
      category: "attack",
      details: "Adversaries may duplicate then impersonate another user's token to escalate privileges and bypass access controls."
    },
    { 
      term: "T1134.002 — Create Process with Token", 
      meaning: "Abusing delegated permission models (service principals, OAuth) to access resources indirectly.", 
      category: "attack",
      details: "Adversaries may create a new process with a different token to escalate privileges and bypass access controls."
    },
    { 
      term: "T1135 — Network Share Discovery", 
      meaning: "Enumerating network shares, mapped drives, and SMB resources to find data and move laterally.", 
      category: "attack",
      details: "Adversaries may look for folders and drives shared on remote systems as a means of identifying sources of information to gather as a precursor for Collection and to identify potential systems of interest for Lateral Movement."
    },
    { 
      term: "T1136 — Create Account", 
      meaning: "Creating, modifying, or abusing user accounts (local, domain, or cloud) for persistence or access.", 
      category: "attack",
      details: "Adversaries may create an account to maintain access to victim systems. With a sufficient level of access, creating such accounts may be used to establish secondary credentialed access that do not require persistent remote access tools to be deployed on the system."
    },
    { 
      term: "T1136.001 — Local Account", 
      meaning: "Creating local or domain user accounts for persistent access.", 
      category: "attack",
      details: "Adversaries may create a local account to maintain access to victim systems. Local accounts are those configured by an organization for use by users, remote support, services, or for administration on a single system or service."
    },
    { 
      term: "T1136.002 — Domain Account", 
      meaning: "Creating service or scheduled accounts intended to run tasks or services stealthily.", 
      category: "attack",
      details: "Adversaries may create a domain account to maintain access to victim systems. Domain accounts are those managed by Active Directory Domain Services where access and permissions are configured across systems and services that are part of that domain."
    },
    { 
      term: "T1136.003 — Cloud Account", 
      meaning: "Using cloud APIs or workflows to create and abuse cloud accounts for persistence.", 
      category: "attack",
      details: "Adversaries may create a cloud account to maintain access to victim systems. With a sufficient level of access, such accounts may be used to establish secondary credentialed access that does not require persistent remote access tools to be deployed on the system."
    },
    { 
      term: "T1137 — Office Application Startup", 
      meaning: "Abusing authentication stores or mechanisms (keys, tokens, SSH authorized_keys) to embed access.", 
      category: "attack",
      details: "Adversaries may leverage Microsoft Office-based applications for persistence between startups. Microsoft Office is a fairly common application suite on Windows-based operating systems within an enterprise network."
    },
    { 
      term: "T1137.001 — Office Template Macros", 
      meaning: "Adding SSH keys or authorized keys to enable re-entry.", 
      category: "attack",
      details: "Adversaries may abuse Microsoft Office templates to obtain persistence on a compromised system. Microsoft Office contains templates that are part of common Office applications and are used to customize styles."
    },
    { 
      term: "T1137.002 — Office Test", 
      meaning: "Injecting tokens or credentials into configurations for persistent access.", 
      category: "attack",
      details: "Adversaries may abuse the Microsoft Office 'Office Test' Registry key to obtain persistence on a compromised system."
    },
    { 
      term: "T1137.004 — Outlook Home Page", 
      meaning: "Abusing delegated auth methods or other platform-specific authentication vectors for persistence.", 
      category: "attack",
      details: "Adversaries may abuse Microsoft Outlook's Home Page feature to obtain persistence on a compromised system."
    },
    { 
      term: "T1140 — Deobfuscate/Decode Files or Information", 
      meaning: "Embedding obfuscated or encrypted payloads in documents or files to evade static detection.", 
      category: "attack",
      details: "Adversaries may use Obfuscated Files or Information to hide artifacts of an intrusion from analysis. They may require separate mechanisms to decode or deobfuscate that information depending on how they intend to use it."
    },
    { 
      term: "T1176 — Browser Extensions", 
      meaning: "Exploiting or abusing wireless or network device misconfigurations to gain access or move laterally.", 
      category: "attack",
      details: "Adversaries may abuse Internet browser extensions to establish persistent access to victim systems. Browser extensions or plugins are small programs that can add functionality and customize aspects of Internet browsers."
    },
    { 
      term: "T1189 — Drive-by Compromise", 
      meaning: "Conducting drive-by compromises where browsing or loading web content leads to compromise.", 
      category: "attack",
      details: "Adversaries may gain access to a system through a user visiting a website over the normal course of browsing. With this technique, the user's web browser is typically targeted for exploitation, but adversaries may also use compromised websites for non-exploitation behavior such as acquiring Application Access Token."
    },
    { 
      term: "T1190 — Exploit Public-Facing Application", 
      meaning: "Exploiting vulnerabilities in internet-facing applications (RCE, injection) for initial access.", 
      category: "attack",
      details: "Adversaries may attempt to take advantage of a weakness in an Internet-facing computer or program using software, data, or commands in order to cause unintended or unanticipated behavior."
    },
    { 
      term: "T1195 — Supply Chain Compromise", 
      meaning: "Leveraging supply-chain or third-party components to introduce malicious code or access.", 
      category: "attack",
      details: "Adversaries may manipulate products or product delivery mechanisms prior to receipt by a final consumer for the purpose of data or system compromise."
    },
    { 
      term: "T1197 — BITS Jobs", 
      meaning: "Using remote file copy or transfer utilities to move tools or files between systems.", 
      category: "attack",
      details: "Adversaries may abuse BITS jobs to persistently execute or clean up after malicious payloads. Windows Background Intelligent Transfer Service (BITS) is a low-bandwidth, asynchronous file transfer mechanism exposed through Component Object Model (COM)."
    },
    { 
      term: "T1203 — Exploitation for Client Execution", 
      meaning: "Exploiting client-side vulnerabilities (browsers, plugins) to achieve code execution.", 
      category: "attack",
      details: "Adversaries may exploit software vulnerabilities in client applications to execute code. Vulnerabilities can exist in software due to unsecure coding practices that can lead to unanticipated behavior."
    },
    { 
      term: "T1204 — User Execution", 
      meaning: "Inducing user interaction (social engineering) to get victims to run code or disclose credentials.", 
      category: "attack",
      details: "An adversary may rely upon specific actions by a user in order to gain execution. Users may be subjected to social engineering to get them to execute malicious code by, for example, opening a malicious document file or link."
    },
    { 
      term: "T1204.001 — Malicious Link", 
      meaning: "Using malicious links that direct users to attacker-controlled pages or actions.", 
      category: "attack",
      details: "An adversary may rely upon a user clicking a malicious link in order to gain execution. Users may be subjected to social engineering to get them to click on a link that will lead to code execution."
    },
    { 
      term: "T1210 — Exploitation of Remote Services", 
      meaning: "Exploiting third-party components or plug-ins with vulnerabilities to escalate privileges or execute code.", 
      category: "attack",
      details: "Adversaries may exploit remote services to gain unauthorized access to internal systems once inside of a network. Exploitation of a software vulnerability occurs when an adversary takes advantage of a programming error in a program, service, or within the operating system software or kernel itself to execute adversary-controlled code."
    },
    { 
      term: "T1218 — Signed Binary Proxy Execution", 
      meaning: "Abusing signed, trusted, or whitelisted binaries and utilities (living-off-the-land) for execution.", 
      category: "attack",
      details: "Adversaries may bypass process and/or signature-based defenses by proxying execution of malicious content with signed binaries."
    },
    { 
      term: "T1218.011 — Rundll32", 
      meaning: "Leveraging a specific legitimate signed component or system utility to execute malicious code.", 
      category: "attack",
      details: "Adversaries may abuse rundll32.exe to proxy execution of malicious code. Using rundll32.exe, vice executing directly (i.e. Shared Modules), may avoid triggering security tools that may not monitor execution of the rundll32.exe process because of allowlists or false positives from normal operations."
    },
    { 
      term: "T1219 — Remote Access Software", 
      meaning: "Using remote services or cloud features to schedule or execute code remotely.", 
      category: "attack",
      details: "An adversary may use legitimate desktop support and remote access software, such as Team Viewer, Go2Assist, LogMein, AmmyyAdmin, etc, to establish an interactive command and control channel to target systems within networks."
    },
    { 
      term: "T1222 — File and Directory Permissions Modification", 
      meaning: "Abusing or bypassing permission checks and policy enforcement to perform privileged actions.", 
      category: "attack",
      details: "Adversaries may modify file or directory permissions/attributes to evade access control lists (ACLs) and access protected files."
    },
    { 
      term: "T1222.001 — Windows File and Directory Permissions Modification", 
      meaning: "Exploiting weak policy or misconfigurations in access controls as a subtechnique.", 
      category: "attack",
      details: "Adversaries may modify file or directory permissions/attributes to evade access control lists (ACLs) and access protected files. File and directory permissions are commonly managed by ACLs configured by the file or directory owner, or users with the appropriate permissions."
    },
    { 
      term: "T1222.002 — Linux and Mac File and Directory Permissions Modification", 
      meaning: "Other variants of permission or policy abuse to gain unauthorized capabilities.", 
      category: "attack",
      details: "Adversaries may modify file or directory permissions/attributes to evade access control lists (ACLs) and access protected files. File and directory permissions are commonly managed by discretionary access control lists (DACLs) configured by the file or directory owner, or users with the appropriate permissions."
    },
    { 
      term: "T1486 — Data Encrypted for Impact", 
      meaning: "Encrypting data on victim systems (ransomware) to deny access and often demand ransom.", 
      category: "attack",
      details: "Adversaries may encrypt data on target systems or on large numbers of systems in a network to interrupt availability to system and network resources. They can attempt to render stored data inaccessible by encrypting files or data on local and remote drives and withholding access to a decryption key."
    },
    { 
      term: "T1490 — Inhibit System Recovery", 
      meaning: "Disrupting system or service availability by destroying data, configurations, or systems.", 
      category: "attack",
      details: "Adversaries may delete or remove built-in operating system data and turn off services designed to aid in the recovery of a corrupted system to prevent recovery."
    },
    { 
      term: "T1505 — Server Software Component", 
      meaning: "Planting backdoors, web shells, or implants to retain long-term access to systems.", 
      category: "attack",
      details: "Adversaries may abuse legitimate extensible development features of servers to establish persistent access to systems. Enterprise server applications may include features that allow developers to write and install software or scripts to extend the functionality of the main application."
    },
    { 
      term: "T1505.003 — Web Shell", 
      meaning: "Installing a web shell on a server to allow remote command execution and persistence.", 
      category: "attack",
      details: "Adversaries may backdoor web servers with web shells to establish persistent access to systems. A Web shell is a Web script that is placed on an openly accessible Web server to allow an adversary to use the Web server as a gateway into a network."
    },
    { 
      term: "T1543 — Create or Modify System Process", 
      meaning: "Installing or abusing system services to achieve persistence and automatic execution.", 
      category: "attack",
      details: "Adversaries may create or modify system-level processes to repeatedly execute malicious payloads as part of persistence."
    },
    { 
      term: "T1543.003 — Windows Service", 
      meaning: "Using nonstandard service installation methods to persist on a target.", 
      category: "attack",
      details: "Adversaries may create or modify Windows services to repeatedly execute malicious payloads as part of persistence. When Windows boots up, it starts programs or applications called services that perform background system functions."
    },
    { 
      term: "T1546 — Event Triggered Execution", 
      meaning: "Leveraging autostart or autorun mechanisms (registry run keys, startup folders) to persist.", 
      category: "attack",
      details: "Adversaries may establish persistence and/or elevate privileges using system mechanisms that trigger execution based on specific events."
    },
    { 
      term: "T1547 — Boot or Logon Autostart Execution", 
      meaning: "Modifying startup or initialization mechanisms (systemd, init scripts, boot entries) for persistence.", 
      category: "attack",
      details: "Adversaries may configure system settings to automatically execute a program during system boot or logon to maintain persistence or gain higher-level privileges on compromised systems."
    },
    { 
      term: "T1547.001 — Registry Run Keys / Startup Folder", 
      meaning: "Modifying platform-specific startup mechanisms (example: systemd unit files) to run code at boot.", 
      category: "attack",
      details: "Adversaries may achieve persistence by adding a program to a startup folder or referencing it with a Registry run key. Adding an entry to the 'run keys' in the Registry or startup folder will cause the program referenced to be executed when a user logs in."
    },
    { 
      term: "T1547.009 — Shortcut Modification", 
      meaning: "Using other platform-specific autostart features as a persistence subtechnique.", 
      category: "attack",
      details: "Adversaries may create or modify shortcuts to run a program during system boot or user login. Shortcuts or symbolic links are ways of referencing other files or programs that will be opened or executed when the shortcut is clicked or executed by a system startup process."
    },
    { 
      term: "T1552 — Unsecured Credentials", 
      meaning: "Accessing or harvesting stored credentials from config files, scripts, or services.", 
      category: "attack",
      details: "Adversaries may search compromised systems to find and obtain insecurely stored credentials. These credentials can be stored and/or misplaced in many locations on a system, including plaintext files (e.g. Bash History), operating system or application-specific repositories (e.g. Credentials in Registry), or other specialized files/artifacts (e.g. Private Keys)."
    },
    { 
      term: "T1552.001 — Credentials In Files", 
      meaning: "Retrieving credentials stored in local files or application configuration.", 
      category: "attack",
      details: "Adversaries may search local file systems and remote file shares for files containing insecurely stored credentials. These can be files created by users to store their own credentials, shared credential stores for a group of individuals, configuration files containing passwords for a system or service, or source code/binary files containing embedded passwords."
    },
    { 
      term: "T1552.004 — Private Keys", 
      meaning: "Extracting secrets from cloud metadata endpoints or service configuration.", 
      category: "attack",
      details: "Adversaries may search for private key certificate files on compromised systems for insecurely stored credentials. Private cryptographic keys and certificates are used for authentication, encryption/decryption, and digital signatures."
    },
    { 
      term: "T1555 — Credentials from Password Stores", 
      meaning: "Targeting credential managers, password vaults, or secure stores to steal credentials.", 
      category: "attack",
      details: "Adversaries may search for common password storage locations to obtain user credentials. Passwords are stored in several places on a system, depending on the operating system or application holding the credentials."
    },
    { 
      term: "T1555.003 — Credentials from Web Browsers", 
      meaning: "Targeting platform-specific credential managers (e.g., macOS keychain, Windows credential manager).", 
      category: "attack",
      details: "Adversaries may acquire credentials from web browsers by reading files specific to the target browser. Web browsers commonly save credentials such as website usernames and passwords so that they do not need to be entered manually in the future."
    },
    { 
      term: "T1556 — Modify Authentication Process", 
      meaning: "Exploiting authentication protocols or services to impersonate users or bypass checks.", 
      category: "attack",
      details: "Adversaries may modify authentication mechanisms and processes to access user credentials or enable otherwise unwarranted access to accounts."
    },
    { 
      term: "T1556.004 — Network Device Authentication", 
      meaning: "Subtechniques that abuse particular authentication systems or tokens.", 
      category: "attack",
      details: "Adversaries may use Patch System Image to hard code a password in the operating system, thus bypassing of native authentication mechanisms for local accounts on network devices."
    },
    { 
      term: "T1558 — Steal or Forge Kerberos Tickets", 
      meaning: "Abusing third-party authentication or federation (SSO) integrations to gain access.", 
      category: "attack",
      details: "Adversaries may attempt to subvert Kerberos authentication by stealing or forging Kerberos tickets to enable Pass the Ticket."
    },
    { 
      term: "T1558.003 — Kerberoasting", 
      meaning: "Targeting specific SSO connectors or federation tokens to impersonate users.", 
      category: "attack",
      details: "Adversaries may abuse a valid Kerberos ticket-granting ticket (TGT) or sniff network traffic to obtain a ticket-granting service (TGS) ticket that may be vulnerable to Brute Force."
    },
    { 
      term: "T1560 — Archive Collected Data", 
      meaning: "Using archive and compression formats to package and transfer data for exfiltration.", 
      category: "attack",
      details: "An adversary may compress and/or encrypt data that is collected prior to exfiltration. Compressing the data can help to obfuscate the collected data and minimize the amount of data sent over the network."
    },
    { 
      term: "T1560.001 — Archive via Utility", 
      meaning: "Using common archive formats (ZIP, TAR) to compress data to avoid detection.", 
      category: "attack",
      details: "An adversary may compress or encrypt data that is collected prior to exfiltration using 3rd party utilities. Many utilities exist that can archive data, including 7-Zip, WinRAR, and WinZip."
    },
    { 
      term: "T1560.002 — Archive via Library", 
      meaning: "Using container or image packaging formats to move or exfiltrate data.", 
      category: "attack",
      details: "An adversary may compress or encrypt data that is collected prior to exfiltration using 3rd party libraries. Many libraries exist that can archive data, including Python rarfile, libzip, and zlib."
    },
    { 
      term: "T1562 — Impair Defenses", 
      meaning: "Evading defenses by altering execution patterns, using runtime manipulation, or disabling defenses.", 
      category: "attack",
      details: "Adversaries may maliciously modify components of a victim's environment in order to hinder or disable defensive mechanisms. This not only involves impairing preventative defenses, such as firewalls and anti-virus, but also detection capabilities that defenders can use to audit activity and identify malicious behavior."
    },
    { 
      term: "T1562.001 — Disable or Modify Tools", 
      meaning: "In-memory runtime injection or hooking to evade signature detection.", 
      category: "attack",
      details: "Adversaries may modify and/or disable security tools to avoid possible detection of their malware/tools and activities. This may take the many forms, such as killing security software processes or services, modifying / deleting Registry keys or configuration files so that tools do not operate properly, or other methods to interfere with security tools scanning or reporting information."
    },
    { 
      term: "T1562.004 — Disable or Modify System Firewall", 
      meaning: "Disabling or modifying defensive features (antivirus, logging) to avoid detection.", 
      category: "attack",
      details: "Adversaries may disable or modify system firewalls in order to bypass controls limiting network usage. Changes could be disabling the entire mechanism as well as adding, deleting, or modifying particular rules."
    },
    { 
      term: "T1562.006 — Indicator Blocking", 
      meaning: "Exploiting environment-specific behaviors to bypass security controls.", 
      category: "attack",
      details: "An adversary may attempt to block indicators or events typically captured by sensors from being gathered and analyzed. This could include maliciously redirecting or even disabling host-based sensors, such as Event Tracing for Windows (ETW), by tampering settings that control the collection and flow of event telemetry."
    },
    { 
      term: "T1566 — Phishing", 
      meaning: "Phishing: delivering deceptive messages (email, social media) that trick users into revealing credentials or executing malware.", 
      category: "attack",
      details: "Adversaries may send phishing messages to gain access to victim systems. All forms of phishing are electronically delivered social engineering. Phishing can be targeted, known as spearphishing."
    },
    { 
      term: "T1566.001 — Spearphishing Attachment", 
      meaning: "Spearphishing via malicious attachments that drop or execute malware when opened.", 
      category: "attack",
      details: "Adversaries may send spearphishing emails with a malicious attachment in an attempt to gain access to victim systems. Spearphishing attachment is a specific variant of spearphishing. Spearphishing attachment is different from other forms of spearphishing in that it employs the use of malware attached to an email."
    },
    { 
      term: "T1566.002 — Spearphishing Link", 
      meaning: "Spearphishing via links that direct victims to credential-harvesting or malware-hosting sites.", 
      category: "attack",
      details: "Adversaries may send spearphishing emails with a malicious link in an attempt to gain access to victim systems. Spearphishing with a link is a specific variant of spearphishing. It is different from other forms of spearphishing in that it employs the use of links to download malware contained in email, instead of attaching malicious files to the email itself, to avoid defenses that may inspect email attachments."
    },
    
    // NIST Control Families
    { 
      term: "AC — Access Control", 
      meaning: "Manage who can access systems and data, ensuring users only have the privileges necessary for their roles.", 
      category: "concept",
      details: "Access Control (AC) controls limit information system access to authorized users, processes acting on behalf of authorized users, or devices (including other information systems) and to the types of transactions and functions that authorized users are permitted to exercise. These controls enforce approved authorizations for logical access to information and system resources in accordance with applicable access control policies."
    },
    { 
      term: "AT — Awareness and Training", 
      meaning: "Ensure personnel are aware of security risks and receive training to perform duties securely.", 
      category: "concept",
      details: "Awareness and Training (AT) controls ensure that managers, system administrators, and users of organizational information systems are made aware of the security risks associated with their activities and of the applicable laws, Executive Orders, directives, policies, standards, instructions, regulations, or procedures related to the security of organizational information systems."
    },
    { 
      term: "AU — Audit and Accountability", 
      meaning: "Record, review, and analyze actions and events to detect and respond to security incidents.", 
      category: "concept",
      details: "Audit and Accountability (AU) controls help organizational personnel monitor the actions of users and processes, reconstruct events when problems occur, and deter inappropriate behavior by making users accountable for their actions. Audit records can be used to support after-the-fact investigations of security incidents and to meet regulatory and organizational audit requirements."
    },
    { 
      term: "CA — Security Assessment and Authorization", 
      meaning: "Periodically assess systems and authorize operation based on risk and compliance.", 
      category: "concept",
      details: "Security Assessment and Authorization (CA) controls focus on the assessment of security controls in information systems and environments of operation to determine the extent to which the controls are implemented correctly, operating as intended, and producing the desired outcome with respect to meeting the security requirements for the system and organization."
    },
    { 
      term: "CM — Configuration Management", 
      meaning: "Manage and control system configurations to prevent unauthorized changes or insecure settings.", 
      category: "concept",
      details: "Configuration Management (CM) controls focus on establishing and maintaining baseline configurations and inventories of organizational information systems (including hardware, software, firmware, and documentation) throughout the respective system development life cycles. Configuration management controls also focus on preventing unauthorized changes to systems and system components and providing an audit trail of changes that do occur."
    },
    { 
      term: "CP — Contingency Planning", 
      meaning: "Prepare for, respond to, and recover from system disruptions or disasters.", 
      category: "concept",
      details: "Contingency Planning (CP) controls focus on establishing, maintaining, and effectively implementing plans for emergency response, backup operations, and post-disaster recovery for organizational information systems to ensure the availability of critical information resources and continuity of operations in emergency situations."
    },
    { 
      term: "IA — Identification and Authentication", 
      meaning: "Verify user identities and control access using secure credentials or multifactor methods.", 
      category: "concept",
      details: "Identification and Authentication (IA) controls focus on identifying users, processes acting on behalf of users, or devices and authenticating (or verifying) the identities of those users, processes, or devices, as a prerequisite to allowing access to organizational information systems. These controls ensure that only authenticated entities can access systems and that their identities are verified before granting access."
    },
    { 
      term: "IR — Incident Response", 
      meaning: "Detect, analyze, and respond to cybersecurity incidents effectively and document recovery steps.", 
      category: "concept",
      details: "Incident Response (IR) controls focus on establishing an operational incident handling capability for organizational information systems that includes adequate preparation, detection, analysis, containment, recovery, and user response activities. Incident response controls also emphasize the importance of information sharing and coordination with external organizations when required."
    },
    { 
      term: "MA — Maintenance", 
      meaning: "Perform regular, secure maintenance on systems while controlling tools and personnel involved.", 
      category: "concept",
      details: "Maintenance (MA) controls focus on performing periodic and timely maintenance on organizational information systems and providing effective controls on the tools, techniques, mechanisms, and personnel used to conduct information system maintenance. These controls ensure that maintenance activities are performed securely and do not compromise system security."
    },
    { 
      term: "MP — Media Protection", 
      meaning: "Protect physical and digital media (e.g., drives, USBs) that contain sensitive data.", 
      category: "concept",
      details: "Media Protection (MP) controls focus on protecting information system media, both paper and digital. These controls limit access to information on information system media to authorized users and provide guidance for media handling, storage, transport, and sanitization. Media protection is essential for preventing unauthorized disclosure of information."
    },
    { 
      term: "PE — Physical and Environmental Protection", 
      meaning: "Safeguard facilities, equipment, and personnel from physical or environmental threats.", 
      category: "concept",
      details: "Physical and Environmental Protection (PE) controls focus on protection of information systems, equipment, and the environment in which the systems operate. Physical and environmental protection controls address physical access to systems, equipment, and the operating environment, and threats such as fire, flood, environmental hazards, and unauthorized intrusion."
    },
    { 
      term: "PL — Planning", 
      meaning: "Develop and maintain policies, procedures, and security plans aligned with organizational goals.", 
      category: "concept",
      details: "Planning (PL) controls focus on developing, documenting, periodically updating, and implementing security plans for organizational information systems that describe the security controls in place or planned for the systems and the rules of behavior for individuals accessing the systems. Planning controls ensure that security requirements are integrated into organizational planning processes."
    },
    { 
      term: "PM — Program Management", 
      meaning: "Establish a governance framework and oversee implementation of security and privacy programs.", 
      category: "concept",
      details: "Program Management (PM) controls focus on organization-wide information security and privacy program management requirements that are independent of any particular information system and are required for effective management of organizational security and privacy programs. These controls provide governance and oversight for security and privacy programs."
    },
    { 
      term: "PS — Personnel Security", 
      meaning: "Screen, train, and monitor personnel to reduce insider threats and unauthorized access.", 
      category: "concept",
      details: "Personnel Security (PS) controls focus on ensuring that individuals occupying positions of responsibility within organizations (including third-party service providers) are trustworthy and meet established security criteria for those positions. Personnel security controls also focus on protecting organizational information and information systems from insider threats."
    },
    { 
      term: "RA — Risk Assessment", 
      meaning: "Identify, evaluate, and prioritize risks to organizational operations and assets.", 
      category: "concept",
      details: "Risk Assessment (RA) controls focus on conducting assessments of risk to organizational operations (including mission, functions, image, or reputation), organizational assets, individuals, other organizations, and the Nation, resulting from the operation of organizational information systems and the associated processing, storage, or transmission of organizational information."
    },
    { 
      term: "SA — System and Services Acquisition", 
      meaning: "Ensure security is built into system development and procurement processes.", 
      category: "concept",
      details: "System and Services Acquisition (SA) controls focus on ensuring that information security requirements are included in information system acquisition contracts and that systems, system components, and information system services are acquired from reputable sources. These controls emphasize the importance of supply chain risk management and secure system development practices."
    },
    { 
      term: "SC — System and Communications Protection", 
      meaning: "Protect system communications and control boundaries through encryption, segmentation, etc.", 
      category: "concept",
      details: "System and Communications Protection (SC) controls focus on providing mechanisms to protect the integrity of the information system and communications. These controls address network security, cryptographic protection, system partitioning, and other protective measures to ensure secure communications and system boundaries."
    },
    { 
      term: "SI — System and Information Integrity", 
      meaning: "Monitor systems to detect and correct flaws, malicious code, or unauthorized changes.", 
      category: "concept",
      details: "System and Information Integrity (SI) controls focus on identifying, reporting, and correcting information and information system flaws in a timely manner; providing protection from malicious code at appropriate locations within organizational information systems; and monitoring information system security alerts and advisories and taking appropriate actions in response."
    },
    { 
      term: "SR — Supply Chain Risk Management", 
      meaning: "Manage security risks arising from suppliers and third-party components.", 
      category: "concept",
      details: "Supply Chain Risk Management (SR) controls focus on managing supply chain risks associated with the research and development, design, manufacturing, acquisition, delivery, integration, operations and maintenance, and disposal of systems and system components. These controls address risks from suppliers, developers, system integrators, and external system service providers."
    },
    { 
      term: "PT — Privacy", 
      meaning: "(NIST Privacy Framework extension) Manage personally identifiable information (PII) with safeguards.", 
      category: "concept",
      details: "Privacy (PT) controls focus on protecting personally identifiable information (PII) and managing privacy risks. These controls are part of the NIST Privacy Framework extension and address privacy requirements including data minimization, purpose limitation, individual participation, data quality and integrity, security, accountability and auditing, and transparency."
    }
  ];

function CybersecurityGlossaryApp() {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [filteredTerms, setFilteredTerms] = useState([]);
  const [expandedTerms, setExpandedTerms] = useState({});

  const filterTerms = useCallback(() => {
    let filtered = terms;

    // Filter by category
    if (selectedCategory !== "all") {
      filtered = filtered.filter(item => item.category === selectedCategory);
    }

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(item => 
        item.term.toLowerCase().includes(query) || 
        item.meaning.toLowerCase().includes(query) ||
        (item.details && item.details.toLowerCase().includes(query))
      );
    }

    setFilteredTerms(filtered);
  }, [searchQuery, selectedCategory]);

  useEffect(() => {
    filterTerms();
  }, [filterTerms]);

  const handleCategoryFilter = (category) => {
    setSelectedCategory(category);
  };

  const toggleTermExpansion = (index) => {
    setExpandedTerms(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  return (
    <div className="container">
      <h1 className="title">Foundation Project Glossary</h1>

      <div className="search-container">
        <input
          type="text"
          placeholder="Search for a term..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="search-input"
        />
      </div>

      <div className="filters">
        <button 
          className={`filter-btn ${selectedCategory === 'all' ? 'active' : ''}`}
          onClick={() => handleCategoryFilter('all')}
        >
          All
        </button>
        <button 
          className={`filter-btn ${selectedCategory === 'flow' ? 'active' : ''}`}
          onClick={() => handleCategoryFilter('flow')}
        >
          Flow Details & Connections
        </button>
        <button 
          className={`filter-btn ${selectedCategory === 'vulnerability' ? 'active' : ''}`}
          onClick={() => handleCategoryFilter('vulnerability')}
        >
          Vulnerabilities
        </button>
        <button 
          className={`filter-btn ${selectedCategory === 'mitigation' ? 'active' : ''}`}
          onClick={() => handleCategoryFilter('mitigation')}
        >
          Mitigations
        </button>
        <button 
          className={`filter-btn ${selectedCategory === 'owasp' ? 'active' : ''}`}
          onClick={() => handleCategoryFilter('owasp')}
        >
          OWASP
        </button>
        <button 
          className={`filter-btn ${selectedCategory === 'nist' ? 'active' : ''}`}
          onClick={() => handleCategoryFilter('nist')}
        >
          NIST
        </button>
        <button 
          className={`filter-btn ${selectedCategory === 'concept' ? 'active' : ''}`}
          onClick={() => handleCategoryFilter('concept')}
        >
          Concepts
        </button>
        <button 
          className={`filter-btn ${selectedCategory === 'attack' ? 'active' : ''}`}
          onClick={() => handleCategoryFilter('attack')}
        >
          MITRE ATT&CK
        </button>
      </div>

      <div className="glossary">
        {filteredTerms.map((item, index) => (
          <div key={index} className="term">
            <div className="term-header">
              <h3>{item.term}</h3>
              <button 
                className="read-more-btn"
                onClick={() => toggleTermExpansion(index)}
              >
                {expandedTerms[index] ? "Read Less" : "Read More"}
              </button>
            </div>
            <p className="term-meaning">{item.meaning}</p>
            {expandedTerms[index] && (
              <div className="term-details">
                <p>{item.details}</p>
              </div>
            )}
          </div>
        ))}
        {filteredTerms.length === 0 && (
          <div className="no-results">
            <p>No terms found matching your search criteria.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default CybersecurityGlossaryApp; 